// src/components/charts/CustomBarChart.js
import React from "react";
import { BarChart } from "@mui/x-charts/BarChart";
import { Typography, Box, useTheme, useMediaQuery } from "@mui/material";

export default function CustomBarChart({
  data,
  filledLabel,
  highestLabel,
  layout = "horizontal",
  mode = "stacked", // "stacked" | "single" | "multi"
}) {
      const theme = useTheme();
      const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
      const effectiveLayout = isSmallScreen ? "vertical" : layout;

  // ✅ more robust check
  const isEmpty =
    !data ||
    data.length === 0 ||
    data.every((d) => Object.values(d).every((v) => v == null));

  if (isEmpty) {
    return (
      <Box display="flex" justifyContent="flex" alignItems="center">
        <Typography variant="body" color="text.secondary">
          No data available.
        </Typography>
      </Box>
    );
  }

  // Dataset and series setup
  let dataset = [];
  let series = [];
  let axisKey = "category";

  if (mode === "stacked") {
    const highest = Math.max(...data.map((d) => d.value));

    dataset = data.map((d) => ({
      category: d.category,
      filled: d.value,
      remaining: highest - d.value,
      highest,
    }));

    series = [
      {
        dataKey: "filled",
        color: "#183021",
        stack: "points",
        label: filledLabel,
        valueFormatter: (value) => value,
      },
      {
        dataKey: "remaining",
        color: "#d9dcc7",
        stack: "points",
        label: highestLabel,
        valueFormatter: () => highest,
      },
    ];
  }

  if (mode === "single") {
    dataset = data;
    axisKey = dataset[0].category !== undefined ? "category" : "month";

    series = [
      {
        dataKey: "value",
        color: "#183021",
        label: filledLabel || "Value",
      },
    ];
  }

  if (mode === "multi") {
    dataset = data;
    axisKey = dataset[0].category !== undefined ? "category" : "month";

    const keys = Object.keys(dataset[0]).filter((k) => k !== axisKey);
    const colors = ["#183021", "#93957cff", "#EAEBDC", "#CBCEA7", "#FFD166"];

    series = keys.map((key, idx) => ({
      dataKey: key,
      color: colors[idx % colors.length],
      label: key,
    }));
  }

  // const xAxis =
  //   layout === "vertical"
  //     ? [
  //       {
  //         scaleType: "band",
  //         dataKey: axisKey,
  //         categoryGapRatio: 0.5,
  //         axisLine: { display: "none" },
  //         tickLine: { display: "none" },
  //         tick: { fontSize: 12 },
  //       },
  //     ]
  //     : [
  //       {
  //         scaleType: "linear",
  //         axisLine: { display: "none" },
  //         tickLine: { display: "none" },
  //       },
  //     ];

  // const yAxis =
  //   layout === "horizontal"
  //     ? [
  //       {
  //         scaleType: "band",
  //         dataKey: axisKey,
  //         categoryGapRatio: 0.6,
  //         axisLine: { display: "none" },
  //         tickLine: { display: "none" },
  //         tick: { fontSize: 12, maxWidth: 140, lineHeight: 1.2 },
  //       },
  //     ]
  //     : [
  //       {
  //         axisLine: { display: "none" },
  //         tickLine: { display: "none" },
  //       },
  //     ];

  return (
  <BarChart
  key={JSON.stringify(dataset)}
  height={300}
  borderRadius={5}
  dataset={dataset}
  layout={effectiveLayout}  // ← responsive layout
  xAxis={[
    effectiveLayout === "horizontal"
      ? { scaleType: "linear", axisLine: { display: "none" }, tickLine: { display: "none" } }
      : {
          scaleType: "band",
          dataKey: axisKey,
          categoryGapRatio: 0.1,
          axisLine: { display: "none" },
          tickLine: { display: "none" },
          tick: { fontSize: 12 },
        },
  ]}
  yAxis={[
    effectiveLayout === "horizontal"
      ? {
          scaleType: "band",
          dataKey: axisKey,
          width: 100,
          categoryGapRatio: 0.3,
          axisLine: { display: "none" },
          tickLine: { display: "none" },
          tick: { fontSize: 12 },
        }
      : { scaleType: "linear", axisLine: { display: "none" }, tickLine: { display: "none" } },
  ]}
  series={series}
  barLabel="value"
  sx={{
    "& .MuiChartsAxis-tickLabel": {
      whiteSpace: "normal",
      overflow: "visible",
      textOverflow: "unset",
    },
    "& .MuiBarLabel-root": {
      fill: "#d9dcc7",
    },
  }}
/>
);
}
